import { observer } from 'mobx-react-lite';
import { useStore } from '@shared/hooks/useStore';
import { Button } from '@shared/components/common/Button';
import { Checkbox } from '@shared/components/common/Checkbox';
import { Trash2, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '@shared/configs/constants';
import { useEffect } from 'react';
import Heading from '@shared/components/common/Heading';
import { formatPrice } from '@shared/utils/utils';
import { toastify } from '@shared/utils/toastify';
import { flowResult } from 'mobx';
import { PUBLICATION_TYPE } from '@shared/types/enums';

export default observer(function CartPage() {
    const navigate = useNavigate();
    const { cartStore } = useStore();
    const { items, toggleItem, toggleAllItems, getSubtotal, getTax, getTotal, fetchCartItems, deleteToCart } = cartStore;

    useEffect(() => {
        fetchCartItems();
    }, []);

    // Calculate allChecked directly from items (reactive with MobX)
    const allChecked = items.length > 0 && items.every(item => item.checked);

    const handleToggleAll = (checked: boolean) => {
        toggleAllItems(checked);
    };

    // call pre-checkout-cart
    const handleProceedToPayment = async () => {
        const checkedItems = items.filter(item => item.checked);
        if (checkedItems.length === 0) {
            toastify('error', 'カートに商品がありません');
            return;
        }
        const res = await flowResult(cartStore.getPreCheckoutCart(checkedItems.map(item => item.publicationId.toString())));
        if (res) {
            navigate(ROUTES.checkout.href, { state: { cartItems: res } });
        }
    };

    const handleDeleteItem = async (publicationId: number) => {
        flowResult(deleteToCart(publicationId));
    };

    const subtotal = getSubtotal();
    const tax = getTax();
    const total = getTotal();

    return (
        <div className='container mx-auto px-4 py-6 md:py-8 space-y-6'>
            {/* Page Title */}

            <div className='flex items-center gap-4 justify-between'>
                <Heading>カート</Heading>
                {/* Back Button */}
                <Button
                    onClick={() => navigate(-1)}
                    className='min-w-40 gap-2'
                >
                    <ArrowLeft size={18} />
                    戻る
                </Button>
            </div>

            <div className='flex flex-col lg:flex-row gap-6 lg:gap-8'>
                {/* Left Section - Product List */}
                <div className='flex-1'>
                    <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
                        {/* Table Header */}
                        <div className='grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200'>
                            <div className='col-span-1 text-center'>
                                <Checkbox
                                    checked={allChecked || false}
                                    onCheckedChange={handleToggleAll}
                                    className='size-5'
                                />
                            </div>
                            <div className='col-span-6 md:col-span-8 font-semibold'>商品名</div>
                            <div className='col-span-5 md:col-span-3 font-semibold hidden md:block text-right md:text-left'>価格（税抜）</div>
                        </div>

                        {/* Product Items */}
                        <div className='divide-y divide-gray-200'>
                            {items.map(item => (
                                <div
                                    key={item.id}
                                    className='grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 transition-colors'
                                >
                                    {/* Checkbox */}
                                    <div className='col-span-1 flex items-center justify-center pt-2 text-center'>
                                        <Checkbox
                                            checked={item.checked || false}
                                            onCheckedChange={() => toggleItem(item.id)}
                                            className='size-5'
                                        />
                                    </div>

                                    {/* Product Image and Info */}
                                    <div className='col-span-9 md:col-span-8 flex items-center gap-4'>
                                        <div className='flex-shrink-0'>
                                            <Link to={`/publications/${item.publicationId}`}>
                                                <div className='group w-20 h-28 md:w-24 md:h-32 bg-gray-100 rounded border border-gray-200 flex items-center justify-center overflow-hidden'>
                                                    <img
                                                        src={item.publication.linkImg}
                                                        alt={item.publication.title}
                                                        className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-200'
                                                    />
                                                </div>
                                            </Link>
                                        </div>
                                        <div className='flex-1 min-w-0 space-y-1.5'>
                                            <Link to={`/publications/${item.publicationId}`} className='inline-flex w-fit hover:text-primary transition-colors'>
                                                <h3 className='font-medium line-clamp-2'>{item.publication.title}</h3>
                                            </Link>

                                            {item.publication.type === PUBLICATION_TYPE.Book && (
                                                <p className='text-xs line-clamp-1'>{`著者：${item.publication.author}`}</p>
                                            )}
                                            <p className='text-sm md:hidden'>{`${formatPrice(item.publication.price)}（税抜）`}</p>
                                        </div>
                                    </div>

                                    {/* Price */}
                                    <div className='col-span-2 md:col-span-3 flex items-center justify-between gap-4'>
                                        <div className='text-right hidden md:block'>
                                            <div className='font-semibold'>
                                                {formatPrice(item.publication.price)}
                                            </div>
                                        </div>
                                        <Button
                                            variant='ghost'
                                            onClick={() => handleDeleteItem(item.publicationId)}
                                            size='icon'
                                            aria-label='削除'
                                            className='hover:text-destructive hover:bg-destructive/10'
                                        >
                                            <Trash2 size={20} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {items.length === 0 && (
                            <div className='p-8 text-center text-gray-500'>
                                カートに商品がありません
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Section - Summary */}
                <div className='lg:w-80 flex-shrink-0'>
                    <div className='sticky top-40 space-y-4'>

                        {/* Payment Summary */}
                        <div className='bg-[#F1F1F1] rounded-lg p-6 space-y-4'>
                            <div className='space-y-8'>
                                <div className='flex justify-between gap-2'>
                                    <span className='shrink-0'>税別支払金額</span>
                                    <span className='font-bold'>
                                        {formatPrice(subtotal)}
                                    </span>
                                </div>
                                <div className='flex justify-between gap-2'>
                                    <span className='shrink-0'>消費税（10%）</span>
                                    <span className='font-bold'>
                                        {formatPrice(tax)}
                                    </span>
                                </div>
                                <div className='border-t border-[#E0E0E0] pt-3 mt-3'>
                                    <div className='flex justify-between gap-2 text-lg font-bold'>
                                        <span className='shrink-0'>お支払い金額</span>
                                        <span>
                                            {formatPrice(total)}
                                        </span>
                                    </div>
                                </div>

                                {/* Proceed to Payment Button */}
                                <Button
                                    onClick={handleProceedToPayment}
                                    disabled={items.filter(item => item.checked).length === 0}
                                    className='min-w-12 md:min-h-14 w-full text-base rounded-lg'
                                    size='lg'
                                >
                                    お支払い手続きへ進む
                                </Button>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
